import { encounterField } from '../../shared/array-types.js';
import { statusRows } from '../statuses/expiry.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { readItemField } from '../compile/item-field-reader.js';
import { appliedStatusRows } from './applied-status-rows.js';
import { record, upper, type Row, type StatusRow } from '../ability/context.js';
import { cachedInventoryRow, cachedInventoryRows } from '../inventory/cached-inventory-trees';
import { projectInventoryStatusLight } from '../compile/inventory-status-light';
import { createIntegerRollupCompiler } from '../compile/integer-layer-rollup';
import { statusProjectionDependencies } from '../compile/status-attack-budget';
import { normalizePhpListish } from '../../shared/php-listish';
import { createFormulaReadContext } from '../compile/formula-read-context';
/** Install only changed encounter/inventory status rows. Members keep the existing derived-cluster projector. */
export function projectNoncreatureStatuses(encounter: Row, added: readonly StatusRow[], removed: readonly { target: string; status_item_id: string; status_id?: string }[],
  campaign: Row | null, definition: (id: string) => Row | null): void {
  const ids = new Set([...added.map(row => upper(row.target)), ...removed.map(row => upper(row.target))]);
  if (!ids.size) return;
  const targets = new Map<string, Row>();
  const eid = upper(encounterField(encounter, 'encounter_id'));
  if (ids.has(eid)) targets.set(eid, encounter);
  for (const target of cachedInventoryRows(encounter)) {
    const id = upper(readInventoryField(target, 'inventory_id')); if (ids.has(id)) targets.set(id, target);
  }
  const saved = new Map(appliedStatusRows(added, campaign).map(change => [change.row_id, change.row]));
  const lightChanged = new Set<string>();
  for (const [id, target] of targets) {
    const before = { ...target };
    let rows = statusRows(target, 'target').filter(row => !removed.some(change => upper(change.target) === id
      && (change.status_id ? upper(readItemField(row, 'status_id')) === upper(change.status_id)
        : upper(readItemField(row, 'status_item_id')) === upper(change.status_item_id))));
    for (const status of added) if (upper(status.target) === id) {
      rows = rows.filter(row => upper(readItemField(row, 'status_id')) !== upper(status.status_id));
      rows.push({ ...status, ...saved.get(upper(status.status_id)), status_target_id: id, status_caster_id: upper(status.caster),
        status_arr: definition(upper(status.status_item_id)) });
    }
    target.statuses = { ...record(target.statuses), target: rows };
    if (readInventoryField(target, 'inventory_id')) {
      const lightRead = createFormulaReadContext(statusProjectionDependencies(target, definition));
      const beforeLight = Number(lightRead.layerValue(target, 'bright_light_dim_light') ?? 0);
      projectInventoryStatusLight(before, target, definition, true);
      if (beforeLight !== Number(lightRead.layerValue(target, 'bright_light_dim_light') ?? 0)) lightChanged.add(id);
    }
  }
  // Equipped inventory light belongs to its holder's one integer family, not a full member compile.
  if (!lightChanged.size) return;
  const changedIds = [...lightChanged];
  for (const member of normalizePhpListish(encounter.encounter_creatures_arr)) {
    const holder = record(member);
    if (!holder || !changedIds.some(id => cachedInventoryRow(holder, id))) continue;
    createIntegerRollupCompiler(statusProjectionDependencies(holder, definition)).delta(holder, ['bright_light_dim_light']);
  }
}
